import { selectRosters } from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { IExtendedRoster } from '@src/features/shift-post-v2/state/shift-post-v2.types'
import { IAcceptanceCriteriaFormValues } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/acceptance-criteria-step.types'
import { useDefaultRosterIds } from '@src/features/shift-post-v2/utils/roster-defaults.utils'
import { IDropdownData } from '@src/models/Common'
import { FormikProps } from 'formik'
import { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'

// Define the dropdown option type
export interface DropdownOption {
  label: string
  value: string
}

export const useRosterSelection = (
  formik: FormikProps<IAcceptanceCriteriaFormValues>,
) => {
  const { values, setValues, validateField } = formik
  const defaultRosterIds = useDefaultRosterIds()
  const [selectedRosters, setSelectedRosters] = useState<IDropdownData[]>(
    values.auto_accept_rosters.map(roster => ({
      value: roster.id,
      label: roster.name,
    })),
  ) // Local state - handles dropdown values
  const rosters = useSelector(selectRosters)

  const setRosterFields = useCallback(
    (selected: IExtendedRoster[]) => {
      setValues(prev => ({
        ...prev,
        auto_accept_rosters: selected,
        roster_ids: selected.map(roster => roster.id),
        auto_accept_roster_ids: selected.map(roster => roster.id),
      }))

      setSelectedRosters(
        selected.map(roster => ({
          value: roster.id,
          label: roster.name,
        })),
      )

      // Explicitly validate the form after state update
      setTimeout(() => {
        validateField('auto_accept_roster_ids')
      }, 0)
    },
    [setValues, validateField],
  )

  // Remove tag
  const handleRemoveTag = (value: number) => {
    const updatedRosters = values.auto_accept_rosters.filter(
      roster => roster.id !== value,
    )
    setRosterFields(updatedRosters)
  }

  // Add dropdown option
  const handleAddDropdownOption = (option: IDropdownData) => {
    const rosterToAdd = rosters.find(roster => roster.id === option.value)
    if (
      rosterToAdd &&
      !values.auto_accept_rosters.some(roster => roster.id === option.value)
    ) {
      // Check if the roster being added is a default roster (Favorites or Worked Before)
      const isDefaultRoster = defaultRosterIds.some(
        defaultRoster => defaultRoster.id === rosterToAdd.id,
      )

      let updatedRosters: IExtendedRoster[]

      if (isDefaultRoster) {
        // If adding a default roster, just add it to existing selection
        updatedRosters = [...values.auto_accept_rosters, rosterToAdd]
      } else {
        // If adding a non-default roster, remove all default rosters and add the new one
        const nonDefaultRosters = values.auto_accept_rosters.filter(
          roster =>
            !defaultRosterIds.some(
              defaultRoster => defaultRoster.id === roster.id,
            ),
        )
        updatedRosters = [...nonDefaultRosters, rosterToAdd]
      }

      setRosterFields(updatedRosters)
    }
  }

  // Filter out already selected options and format as IDropdownData
  const availableRosterOptions: IDropdownData[] = rosters
    .filter(
      opt => !values.auto_accept_rosters.some(roster => roster.id === opt.id),
    )
    .map(roster => ({
      value: roster.id,
      label: roster.name,
    }))

  return {
    selectedRosters,
    setSelectedRosters,
    availableRosterOptions,
    handleRemoveTag,
    handleAddDropdownOption,
  }
}
